import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Button } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioMedicoBase } from '@/features/registro/medicos/relatorios/relatorio-medico.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { Pesquisa } from '@/shared/components/pessoa/pesquisa/pesquisa';
import { CardMedicoSelecionado } from '@/features/registro/medicos/relatorios/components/card-medico-selecionado/card-medico-selecionado';
import { CertidaoQuitacaoDebitoMedicoService } from '@/core/api/sicom-api/registro/medico/relatorios/certidao-quitacao-debito-medico/certidao-quitacao-debito-medico.service';
import { ReportCertidaoQuitacaoDebitoMedicoRequest, ReportCertidaoQuitacaoDebitoMedicoResponse } from '@/core/api/sicom-api/registro/medico/relatorios/certidao-quitacao-debito-medico/certidao-quitacao-debito-medico.model';
import { CertidaoQuitacaoDebitoMedicoJobService } from '@/core/services/certidao-quitacao-debito-medico-job.service';
import { CanDirective } from '@/core/permissions/can.directive';

type Req = ReportCertidaoQuitacaoDebitoMedicoRequest;
type Resp = ReportCertidaoQuitacaoDebitoMedicoResponse;

@Component({
    selector: 'app-certidao-quitacao-debito-medico',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, TooltipModule, RelatorioContainer, Pesquisa, CardMedicoSelecionado, CanDirective],
    templateUrl: './certidao-quitacao-debito-medico.html',
    styleUrl: './certidao-quitacao-debito-medico.scss'
})
/**
 * Relatório PDF assíncrono da Certidão de Quitação de Débito do médico (nuCrm/sgUf, sem assinante).
 * Herda `RelatorioMedicoBase`: toda a orquestração de export/polling/download/erro e o card
 * "médico selecionado + Alterar" vêm da base; aqui só delegamos o HTTP ao service.
 */
export class ReportCertidaoQuitacaoDebitoMedicoComponent extends RelatorioMedicoBase<Req, Resp> {
    private readonly certidaoService = inject(CertidaoQuitacaoDebitoMedicoService);
    readonly certidaoJobService = inject(CertidaoQuitacaoDebitoMedicoJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.certidaoJobService.job$;

    protected get jobFacade(): RelatorioJobFacade {
        return this.certidaoJobService;
    }

    /** Monta o request a partir do médico selecionado; retorna null (e avisa) se faltar CRM/UF. */
    protected buildRequest(): Req | null {
        return this.resolverIdentificacaoMedico('Selecione um médico (CRM/UF) antes de gerar a certidão.');
    }

    protected exportar(request: Req) {
        return this.certidaoService.exportarPdf(request);
    }

    /** Limpa o job/preview corrente (a certidão não tem formulário de dados a resetar). */
    limparFormulario(): void {
        this.limparJob();
    }
}
